import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, FileText, Image as ImageIcon, Clock, CheckCircle2, XCircle, Search, ChevronRight, RefreshCw, Loader2, WifiOff } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { PortalSheetContent } from '../PortalSheetContent';
import { usePortalDestination } from '../PortalDestinationContext';
import { PortalRequestDestination } from '../PortalIdentity';
import { PortalRxTabs } from '../PortalRxTabs';
import { PortalEmptyState } from '../ui/PortalEmptyState';
import { errorText } from '../health/healthConfig';
import { PortalPrescriptionUploadFlow } from './PortalPrescriptionUploadFlow';

const STATUS_META = {
  pending:   { label: 'Awaiting review',   icon: Clock,        cls: 'bg-amber-100 text-amber-700' },
  scanned:   { label: 'With pharmacist',   icon: Clock,        cls: 'bg-sky-100 text-sky-700' },
  reviewed:  { label: 'Approved',          icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700' },
  rejected:  { label: 'Not accepted',      icon: XCircle,      cls: 'bg-red-100 text-red-700' },
  collected: { label: 'Collected',         icon: CheckCircle2, cls: 'bg-slate-100 text-slate-600' },
  cancelled: { label: 'Cancelled',         icon: XCircle,      cls: 'bg-slate-100 text-slate-600' },
};

/** Builds a timeline strictly from existing status fields — no invented steps or turnaround times. */
function buildTimeline(r) {
  const steps = [{ key: 'uploaded', title: 'Uploaded', at: r.created_at, cls: '' }];
  if (r.scanned_at || r.review?.reviewed_at) {
    steps.push({ key: 'scanned', title: 'Received by your pharmacist', at: r.scanned_at, cls: '' });
  }
  if (r.review?.reviewed_at) {
    steps.push({
      key: 'review',
      title: r.status === 'rejected' ? 'Not accepted' : 'Approved',
      at: r.review.reviewed_at,
      note: r.review.note,
      by: r.review.reviewer_name,
      cls: r.status === 'rejected' ? 'is-rejected' : '',
    });
  } else if (r.status === 'scanned') {
    steps.push({ key: 'in_review', title: 'With your pharmacist', at: null, cls: 'is-pending' });
  } else if (r.status === 'pending') {
    steps.push({ key: 'pending', title: 'Awaiting pharmacist review', at: null, cls: 'is-pending' });
  }
  return steps;
}

function RxDetailSheet({ rx, onClose, onReorder, reordering, branch }) {
  if (!rx) return null;
  const meta = STATUS_META[rx.status] || STATUS_META.pending;
  const timeline = buildTimeline(rx);
  const branchMismatch = !branch || branch.tenant_id !== rx.tenant_id;
  return (
    <Sheet open={!!rx} onOpenChange={(next) => { if (!next) onClose(); }}>
      <PortalSheetContent side="bottom" className="portal-mobile-sheet portal-rx-detail-sheet" data-testid="portal-rx-detail-sheet"
        closeProps={{ 'data-testid': 'portal-rx-detail-close', 'aria-label': 'Close prescription detail' }}>
        <div className="portal-sheet-handle" aria-hidden="true" />
        <SheetTitle className="text-left break-words" data-testid="portal-rx-detail-title">{rx.original_filename || 'Prescription'}</SheetTitle>
        <SheetDescription className="sr-only">Prescription status and history</SheetDescription>
        <div className="space-y-4 mt-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={meta.cls}><meta.icon className="w-3 h-3 mr-1" />{meta.label}</Badge>
            <span className="text-sm text-slate-600" data-testid="portal-rx-detail-branch">{rx.branch_name}</span>
          </div>
          {rx.notes && (
            <div className="text-sm">
              <div className="text-xs font-semibold text-slate-500 mb-1">Your note</div>
              <p className="text-slate-700">{rx.notes}</p>
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-2">History</div>
            <div className="portal-rx-timeline" data-testid="portal-rx-timeline">
              {timeline.map((step) => (
                <div key={step.key} className={`portal-rx-timeline-item ${step.cls}`} data-testid={`portal-rx-timeline-${step.key}`}>
                  <div className="portal-rx-timeline-dot" aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="portal-rx-timeline-title">{step.title}</div>
                    {step.at && <div className="portal-rx-timeline-meta">{new Date(step.at).toLocaleString()}{step.by ? ` · ${step.by}` : ''}</div>}
                    {step.note && <div className="portal-rx-timeline-note">Pharmacist: {step.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {rx.status === 'collected' && (
            <div>
              <Button onClick={() => onReorder(rx)} disabled={reordering || branchMismatch}
                title={branchMismatch ? 'Select this branch above to reorder' : 'Send this prescription again'}
                className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-rx-detail-reorder">
                {reordering ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Reorder — send this document again
              </Button>
              <p className="text-xs text-slate-500 mt-1.5 text-center">
                {branchMismatch ? 'Select this branch above to reorder.' : 'Resends the same prescription to your pharmacist for review.'}
              </p>
            </div>
          )}
        </div>
      </PortalSheetContent>
    </Sheet>
  );
}

export default function PortalPrescriptionsPage() {
  const { api: portalApi, branch } = usePortalDestination();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flowOpen, setFlowOpen] = useState(new URLSearchParams(location.search).get('upload') === '1');
  const [detailRx, setDetailRx] = useState(null);
  const [reorderSeed, setReorderSeed] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const r = await portalApi.get('/prescriptions');
      setRows(r.data.prescriptions || []);
    } catch (e) { setLoadError(errorText(e)); }
    finally { setLoading(false); }
  }, [portalApi]);
  useEffect(() => { load(); }, [load]);

  const closeFlow = useCallback(() => {
    setFlowOpen(false);
    setReorderSeed(null);
    if (location.search) navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // "Reorder" on a collected prescription — re-fetches the same stored file
  // as a File object so the upload flow can resend it without the patient
  // picking it from their device again.
  const reorder = useCallback(async (rx) => {
    setReordering(true);
    try {
      const res = await portalApi.get(`/prescriptions/${rx.rx_id}/file`, { responseType: 'blob' });
      const file = new File([res.data], rx.original_filename || 'prescription', {
        type: res.headers?.['content-type'] || rx.content_type || 'application/octet-stream',
      });
      setReorderSeed(file);
      setFlowOpen(true);
      setDetailRx(null);
    } catch (e) {
      toast.error("Couldn't load that file to resend — try uploading it again instead.");
    } finally {
      setReordering(false);
    }
  }, [portalApi]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!q) return true;
      return (r.original_filename || '').toLowerCase().includes(q) || (r.branch_name || '').toLowerCase().includes(q);
    });
  }, [rows, search, statusFilter]);

  if (flowOpen) {
    return <PortalPrescriptionUploadFlow onClose={closeFlow} onUploaded={load} initialFile={reorderSeed} />;
  }

  return (
    <div className="space-y-4" data-testid="portal-prescriptions-page">
      <PortalRxTabs active="prescriptions" />
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-slate-900">Prescriptions</h1>
        <Button onClick={() => setFlowOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-rx-upload-btn">
          <Upload className="w-4 h-4 mr-1" /> Upload
        </Button>
      </div>
      <PortalRequestDestination testId="portal-rx-destination" />

      <div className="portal-rx-filter-bar" data-testid="portal-rx-filter-bar">
        <div className="relative portal-rx-search">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search prescriptions"
            className="pl-9" data-testid="portal-rx-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] shrink-0" data-testid="portal-rx-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="portal-rx-status-all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <SelectItem key={key} value={key} data-testid={`portal-rx-status-${key}`}>{meta.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="portal-rx-loading">Loading prescriptions…</p>
      ) : loadError ? (
        <PortalEmptyState icon={WifiOff} title="Couldn't load your prescriptions" description={loadError}
          testId="portal-rx-load-error"
          action={<Button variant="outline" size="sm" onClick={load} data-testid="portal-rx-retry">Try again</Button>} />
      ) : filtered.length === 0 ? (
        <PortalEmptyState
          icon={FileText}
          title={rows.length === 0 ? 'No prescriptions yet' : 'No matches'}
          description={rows.length === 0 ? 'Upload a prescription and your pharmacist will review it here.' : 'Try a different search or status.'}
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const Icon = (r.content_type || '').startsWith('image/') ? ImageIcon : FileText;
            const updatedAt = r.review?.reviewed_at || r.scanned_at || r.created_at;
            return (
              <button type="button" key={r.rx_id} className="portal-rx-row" data-testid={`portal-rx-${r.rx_id}`}
                onClick={() => setDetailRx(r)}>
                <div className="portal-rx-row-icon"><Icon className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-medium truncate text-slate-900">{r.original_filename || 'Prescription'}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{new Date(updatedAt).toLocaleString()} · <span data-testid={`portal-rx-location-${r.rx_id}`}>{r.branch_name}</span></div>
                </div>
                <Badge className={meta.cls}><meta.icon className="w-3 h-3 mr-1" />{meta.label}</Badge>
                <ChevronRight size={16} className="portal-rx-chevron" />
              </button>
            );
          })}
        </div>
      )}
      <RxDetailSheet rx={detailRx} onClose={() => setDetailRx(null)} onReorder={reorder} reordering={reordering} branch={branch} />
    </div>
  );
}
